// apps/web/src/components/TransporterVehiclePrompt.tsx
//
// Prompt modal for transporters who have not yet added a vehicle.
// Shows after initial registration or when verified/on dashboard,
// informing them that having an approved vehicle is mandatory to take jobs.

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Truck, ArrowRight, X, ShieldCheck } from 'lucide-react';

import { trpc } from '@/trpc/client';
import { useAuthStore } from '@/store/authStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const TransporterVehiclePrompt: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Target all users with the TRANSPORTER role
  const isTransporter = user?.role === 'TRANSPORTER';

  // Do not prompt if user is already on the vehicle management page or auth pages
  const isExcludedPage =
    location.pathname === '/dashboard/my-vehicles' ||
    location.pathname === '/login' ||
    location.pathname === '/register';

  // Session-scoped dismissal key per user
  const sessionDismissKey = user ? `kc_transporter_vehicle_prompt_${user.id}` : null;

  // Query vehicles for this transporter
  const { data: vehicles, isSuccess, isLoading } = trpc.vehicle.getMyVehicles.useQuery(undefined, {
    enabled: isTransporter && !isExcludedPage,
    staleTime: 1000 * 30, // 30 seconds
  });

  useEffect(() => {
    if (!isTransporter || isExcludedPage || !isSuccess) return;

    // If dismissed in this session, do not show again
    if (sessionDismissKey && sessionStorage.getItem(sessionDismissKey)) {
      return;
    }

    // Only prompt if the transporter has zero registered vehicles
    const hasNoVehicles = !vehicles || vehicles.length === 0;

    if (hasNoVehicles) {
      const timer = setTimeout(() => {
        setOpen(true);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isTransporter, isExcludedPage, isSuccess, vehicles, sessionDismissKey]);

  const dismiss = () => {
    if (sessionDismissKey) {
      sessionStorage.setItem(sessionDismissKey, '1');
    }
    setOpen(false);
  };

  const handleAddVehicle = () => {
    if (sessionDismissKey) {
      sessionStorage.setItem(sessionDismissKey, '1');
    }
    setOpen(false);
    navigate('/dashboard/my-vehicles');
  };

  if (!isTransporter || isExcludedPage) return null;

  const isVerified = user?.verificationStatus === 'VERIFIED';
  const isOnboarding = location.pathname.startsWith('/onboarding');

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) dismiss(); }}>
      <DialogContent
        className="max-w-md w-[calc(100%-2rem)] rounded-2xl p-0 overflow-hidden shadow-2xl border border-border"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Accent banner */}
        <div className="h-1.5 w-full bg-gradient-to-r from-primary via-primary/80 to-primary/40" />

        <div className="p-6">
          {/* Close button */}
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>

          <DialogHeader className="mb-5 text-left">
            {/* Icon */}
            <div className="mb-4 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Truck className="w-6 h-6 text-primary" />
            </div>

            <DialogTitle className="text-xl font-bold text-foreground leading-snug">
              Vehicle Registration Required
            </DialogTitle>

            <DialogDescription className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {isVerified
                ? 'Your account is verified. To start receiving, bidding on, and delivering loads, you must register at least one approved vehicle.'
                : 'As a transporter, having an approved vehicle is mandatory before you can take or accept delivery jobs. You can add your vehicle and documents now so they are reviewed alongside your profile.'}
            </DialogDescription>
          </DialogHeader>

          {/* Requirements summary */}
          <div className="rounded-xl bg-muted/50 border border-border/60 p-3.5 mb-5 space-y-2">
            {[
              'Required to match with available client loads',
              'Mandatory for submitting trip bids and pricing',
              'Vehicle documents are verified by our team',
            ].map((text) => (
              <div key={text} className="flex items-start gap-2.5 text-xs text-foreground/90">
                <span className="mt-0.5 w-4 h-4 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                </span>
                <span>{text}</span>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2.5">
            <Button
              className="flex-1 gap-2 rounded-xl font-medium"
              onClick={handleAddVehicle}
            >
              Add Vehicle Now
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              className="flex-1 rounded-xl text-muted-foreground"
              onClick={dismiss}
            >
              {isOnboarding ? 'Continue Verification First' : 'Remind Me Later'}
            </Button>
          </div>

          <p className="mt-3.5 text-center text-xs text-muted-foreground">
            You can manage your fleet anytime under My Vehicles in your dashboard.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransporterVehiclePrompt;
